import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { User, Mail, Shield, Building2, Download, DatabaseBackup, FileLock2, LogOut, ChevronRight, KeyRound, Bell } from "lucide-react";
import PreStartChecklistSettings from "@/components/PreStartChecklistSettings";

export default function Settings() {
  const { user, fleet, logout, isManager } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    if (isManager) api.get("/me/preferences").then(({ data }) => setPrefs(data)).catch(() => {});
  }, [isManager]);

  const togglePref = async (key, val) => {
    setPrefs((p) => ({ ...p, [key]: val }));
    try {
      const { data } = await api.patch("/me/preferences", { [key]: val });
      setPrefs(data);
      toast.success("Preference saved");
    } catch {
      toast.error("Could not save preference");
      api.get("/me/preferences").then(({ data }) => setPrefs(data)).catch(() => {});
    }
  };

  const row = (Icon, label, val) => (
    <div className="flex items-center gap-4 px-5 py-4 border-b border-white/10 last:border-0">
      <Icon className="w-5 h-5 text-[#FFB800]" />
      <div className="flex-1">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{label}</p>
        <p className="font-body font-700 text-lg">{val}</p>
      </div>
    </div>
  );

  const exportData = async (format) => {
    setBusy(format);
    try {
      const { data, headers } = await api.get(`/export/fleet?format=${format}`, { responseType: "blob" });
      const disposition = headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `ironlog_backup.${format === "csv" ? "zip" : "json"}`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Export failed");
    } finally {
      setBusy("");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update password");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="p-5 md:p-10 pb-24 max-w-2xl" data-testid="settings-page">
      <PageHead title="Account" sub="Your details and fleet." />
      <div className="card">
        {row(User, "Name", user?.name)}
        {row(Mail, "Email", user?.email)}
        {row(Shield, "Role", user?.role)}
        {row(Building2, "Fleet", fleet?.name)}
      </div>
      <p className="font-body text-sm text-[#94A3B8] mt-4">Need to change something? Contact your fleet manager or support.</p>

      {isManager && (
        <div className="mt-10" data-testid="notifications-section">
          <div className="flex items-center gap-3 mb-3">
            <Bell className="w-5 h-5 text-[#FFB800]" />
            <h2 className="font-head font-900 uppercase tracking-tight text-xl">Email Notifications</h2>
          </div>
          <div className="card divide-y divide-white/10">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="pr-4">
                <p className="font-body font-700">Daily overdue email</p>
                <p className="font-body text-sm text-[#94A3B8]">A 7am (AEST) summary of overdue work orders and reminders.</p>
              </div>
              <Switch checked={prefs?.daily_digest_enabled ?? true} onCheckedChange={(v) => togglePref("daily_digest_enabled", v)} disabled={!prefs} data-testid="toggle-daily-digest" />
            </div>
            <div className="flex items-center justify-between px-5 py-4">
              <div className="pr-4">
                <p className="font-body font-700">Weekly Monday summary</p>
                <p className="font-body text-sm text-[#94A3B8]">A Monday-morning recap of the week&apos;s completed and outstanding jobs.</p>
              </div>
              <Switch checked={prefs?.weekly_summary_enabled ?? true} onCheckedChange={(v) => togglePref("weekly_summary_enabled", v)} disabled={!prefs} data-testid="toggle-weekly-summary" />
            </div>
          </div>
        </div>
      )}

      {isManager && <PreStartChecklistSettings />}

      <div className="mt-10" data-testid="change-password-section">
        <div className="flex items-center gap-3 mb-3">
          <KeyRound className="w-5 h-5 text-[#FFB800]" />
          <h2 className="font-head font-900 uppercase tracking-tight text-xl">Change Password</h2>
        </div>
        <form onSubmit={changePassword} className="card p-5 space-y-4" data-testid="change-password-form">
          <div><label className="il-label">Current Password</label><input className="il-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="current-password" placeholder="••••••••" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="il-label">New Password</label><input className="il-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required minLength={6} data-testid="new-password" placeholder="••••••••" /></div>
            <div><label className="il-label">Confirm New</label><input className="il-input" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required minLength={6} data-testid="confirm-password" placeholder="••••••••" /></div>
          </div>
          <button className="btn-signal h-12 px-6 font-head text-sm" disabled={pwBusy} data-testid="change-password-submit">{pwBusy ? "Updating…" : "Update Password"}</button>
        </form>
      </div>

      <div className="mt-10" data-testid="data-export-section">
        <div className="flex items-center gap-3 mb-3">
          <DatabaseBackup className="w-5 h-5 text-[#FFB800]" />
          <h2 className="font-head font-900 uppercase tracking-tight text-xl">Data Backup</h2>
        </div>
        <p className="font-body text-sm text-[#94A3B8] mb-5 max-w-lg">
          Download a complete copy of your fleet data — every machine, service log, reminder and team member.
          Keep it somewhere safe so you always hold your own backup.
        </p>
        <div className="card p-5 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => exportData("json")}
            disabled={!!busy}
            data-testid="export-json-btn"
            className="btn-primary h-12 px-5 flex items-center justify-center gap-2 flex-1 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {busy === "json" ? "Preparing…" : "Download JSON"}
          </button>
          <button
            onClick={() => exportData("csv")}
            disabled={!!busy}
            data-testid="export-csv-btn"
            className="btn-ghost h-12 px-5 flex items-center justify-center gap-2 flex-1 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {busy === "csv" ? "Preparing…" : "Download CSV (.zip)"}
          </button>
        </div>
        <p className="font-mono text-[11px] text-[#94A3B8] mt-3">
          JSON = full structured backup · CSV = spreadsheet-friendly files (machines, service logs, reminders, team) in a zip.
        </p>
      </div>

      <div className="mt-10" data-testid="legal-section">
        <div className="flex items-center gap-3 mb-3">
          <FileLock2 className="w-5 h-5 text-[#FFB800]" />
          <h2 className="font-head font-900 uppercase tracking-tight text-xl">Legal</h2>
        </div>
        <Link
          to="/privacy"
          data-testid="settings-privacy-link"
          className="card px-5 py-4 flex items-center justify-between hover:bg-[#1A1C23] transition-colors"
        >
          <span className="font-body font-700">Privacy Policy</span>
          <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
        </Link>
        <Link
          to="/terms"
          data-testid="settings-terms-link"
          className="card px-5 py-4 flex items-center justify-between hover:bg-[#1A1C23] transition-colors mt-3"
        >
          <span className="font-body font-700">Terms of Service</span>
          <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
        </Link>
      </div>

      <div className="mt-10" data-testid="account-actions-section">
        <button
          onClick={() => { logout(); nav("/login"); }}
          data-testid="settings-logout-btn"
          className="btn-ghost w-full sm:w-auto h-12 px-6 font-head text-sm flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" /> Log Out
        </button>
      </div>
    </div>
  );
}
